import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileSignature, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import useContractReport from "@/components/performance/contracts/useContractReport";
import { RangeSelector, CompareSelector } from "@/components/performance/Selectors";
import { SectionActivity } from "@/components/performance/contracts/SectionActivity";
import { SectionSignature } from "@/components/performance/contracts/SectionSignature";
import { SectionNegotiation } from "@/components/performance/contracts/SectionNegotiation";
import { SectionHealth } from "@/components/performance/contracts/SectionHealth";
import { translateFr as tfr } from "@/i18n";

const Skeleton = () => (
  <div className="space-y-10" data-testid="contract-report-loading">
    {[0, 1, 2].map((i) => (
      <div key={i} className="rounded-2xl border border-border bg-surface h-[300px] animate-pulse" />
    ))}
  </div>
);

export default function ContractPerformance() {
  const { state, setState, data, loading, error, reload } = useContractReport();

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6 pt-6 pb-16" data-testid="contract-performance-page">
      <header className="space-y-3 mb-10">
        <Link to="/performance" data-testid="contract-perf-back"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Performance
        </Link>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">{tfr("Performance des contrats")}</h1>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-lg">
              {tfr("Mesurez l'efficacité, la fiabilité et l'évolution de votre activité contractuelle.")}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <RangeSelector state={state} periodLabel={data?.period?.label} onChange={setState} />
            <CompareSelector state={state} onChange={setState} />
          </div>
        </div>
        {data?.comparisonPeriod && (
          <p className="text-xs text-muted-foreground" data-testid="comparison-period-label">
            Comparé à : {data.comparisonPeriod.label}
          </p>
        )}
      </header>

      {error ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground" role="alert" data-testid="contract-report-error">
          Impossible de charger le rapport de performance contractuelle.
          <Button variant="outline" size="sm" className="ml-3" onClick={reload}><RefreshCw className="h-3.5 w-3.5 mr-1.5" />{tfr("Réessayer")}</Button>
        </div>
      ) : loading && !data ? (
        <Skeleton />
      ) : data && !data.hasData ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center" data-testid="contract-report-empty">
          <FileSignature className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm font-medium">{tfr("Pas encore de données contractuelles")}</p>
          <p className="text-xs text-muted-foreground mt-1 mb-4">{tfr("Créez vos premiers contrats pour générer votre rapport d'intelligence contractuelle.")}</p>
          <Button size="sm" asChild><Link to="/contracts" data-testid="empty-goto-contracts">{tfr("Ouvrir les contrats")}</Link></Button>
        </div>
      ) : data ? (
        <div className="space-y-14">
          <SectionActivity activity={data.activity} />
          <SectionSignature signature={data.signature} />
          <SectionNegotiation negotiation={data.negotiation} />
          <SectionHealth health={data.health} />
          <p className="text-xs text-muted-foreground/70 text-center pt-2" data-testid="phase2-footer-note">
            {tfr("Renouvellements · Performance financière · Score de conformité · Athlynx Intelligence (IA) · Export PDF — arrivent en Phase 2 de ce module.")}
          </p>
        </div>
      ) : null}
    </div>
  );
}
